// src/pages/LoginPage.tsx
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authApi } from "../lib/auth-api";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const next = params.get("next") ?? "/";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await authApi.login({ email, password });
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(next);
    } catch (err) {
      setError((err as Error).message);
    } finally { setBusy(false); }
  };

  return (
    <AuthLayout title="ログイン">
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label="メールアドレス">
          <input
            type="email" autoComplete="username" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input" placeholder="you@saitama-sekishinkai.org"
          />
        </Field>
        <Field label="パスワード">
          <input
            type="password" autoComplete="current-password" required value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
          />
        </Field>
        {error && <div className="auth-error">{error}</div>}
        <button type="submit" disabled={busy} className="auth-button">
          {busy ? "ログイン中…" : "ログイン"}
        </button>
      </form>
      <div className="mt-4 text-center text-sm space-x-3">
        <Link to="/signup" className="text-blue-700 hover:underline">新規登録</Link>
        <span className="text-slate-300">|</span>
        <Link to="/forgot" className="text-blue-700 hover:underline">パスワードを忘れた</Link>
      </div>
    </AuthLayout>
  );
}

// ── 共通 UI (本ファイル内に閉じ込め) ─────────────────────────────────
export function AuthLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm w-full max-w-md p-6">
        <h1 className="text-lg font-semibold text-slate-800 mb-1">{title}</h1>
        <p className="text-xs text-slate-500 mb-5">病院実績ダッシュボード</p>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
